import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { API_CONFIG } from '../../config/api';
import { AUTH_STORAGE_KEYS } from '../../config/auth';

// Create a base query with automatic retry
const baseQuery = fetchBaseQuery({
  baseUrl: API_CONFIG.BASE_URL,
  prepareHeaders: (headers) => {
    // Get token from localStorage or cookies
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.token);
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
  credentials: 'include', // Include cookies in requests
});

// Retry queries only. A mutation that failed may already have taken effect on
// the server, so replaying it can e.g. queue the same device command twice.
const baseQueryWithRetry = retry(baseQuery, {
  retryCondition: (_error, _args, { attempt, baseQueryApi }) =>
    baseQueryApi.type === 'query' && attempt <= 2,
});

// Create a custom base query with refresh-on-401 handling.
const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQueryWithRetry(args, api, extraOptions);

  // Handle 401 Unauthorized errors. Don't act while on the login page
  // (e.g. a wrong-password attempt) — the mutation surfaces that error itself.
  if (result.error && result.error.status === 401
      && !window.location.pathname.includes('/login')) {
    // Try a single token refresh, then replay the original request. Modules are
    // imported lazily to avoid a load-time circular dependency
    // (baseApi → tokenRefresh/sessionTeardown → store/index → baseApi) and so a
    // chunk-load failure (e.g. a stale tab after a redeploy) can't swallow the
    // 401 result — we still return it below.
    try {
      const { refreshAccessToken } = await import('../../services/tokenRefresh');
      await refreshAccessToken();
      // prepareHeaders re-reads the refreshed token from localStorage.
      result = await baseQueryWithRetry(args, api, extraOptions);
    } catch {
      // Refresh failed — the session is genuinely invalid; tear it down below.
    }

    if (result.error && result.error.status === 401) {
      try {
        const { clearSession } = await import('../../services/sessionTeardown');
        clearSession();
      } catch {
        // Best-effort teardown; the caller still receives the 401 error.
      }
    }
  }

  return result;
};

// Create the base API slice
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: customBaseQuery,
  tagTypes: ['Device', 'Command', 'User', 'Users', 'System', 'Dashboard', 'DotNetAppUpdate', 'AuditLogs', 'ClientUpdate', 'Deployment', 'NonTpmDevice', 'DfoGateway'],
  endpoints: () => ({}),
});

// Hooks will be exported from individual API slices
